import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Popup from "../../components/ui/SuccessPopup";
import { useBlogUpdate } from "../../contexts/blog/BlogUpdateContext";

// Quill config (same as AddBlog)
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    [{ color: [] }, { background: [] }],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "align",
  "list",
  "bullet",
  "indent",
  "color",
  "background",
  "link",
  "image",
  "video",
];

export default function EditBlogPost() {
  const { slug } = useParams();

  const {
    formData,
    setFormData,
    errors,
    popup,
    setPopup,
    loading,
    handleInputChange,
    handleImageChange,
    handleAltTextChange,
    handleAddContent,
    handleAddFAQ,
    handleDeleteContent,
    handleDeleteFAQ,
    handleEditorChange,
    handleUpdateBlog,
    setSlug, // context should expose this (as discussed earlier)
  } = useBlogUpdate();

  // inject slug into context when this page mounts
  useEffect(() => {
    if (slug && typeof setSlug === "function") {
      setSlug(slug);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!formData) {
    return <div className="p-10 text-center text-lg">Loading blog details...</div>;
  }

  const onSubmit = () => {
    // console.log("fun call");
    
    handleUpdateBlog();
  };

  return (
    <>
      <div className="max-w-5xl mx-auto p-8 bg-white rounded-xl shadow-lg">
        <h1 className="text-4xl font-extrabold mb-8 text-gray-900 border-b pb-4">
          Edit Blog Post
        </h1>

        {/* Hero Image */}
        <div className="mb-6">
          <label className="block mb-2 text-lg font-semibold text-gray-700">Hero Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleImageChange(e, null, "HeroImg")}
            className={`block w-full text-gray-700 ${errors.HeroImg ? "border border-red-500" : ""}`}
          />
          {errors.HeroImg && <p className="text-red-600 mt-1">{errors.HeroImg}</p>}

          {formData.HeroImg && typeof formData.HeroImg === "string" && (
            <img
              src={formData.HeroImg}
              alt={formData.HeroAltText || "Hero Preview"}
              className="w-56 h-56 mt-4 object-cover rounded-lg border shadow"
            />
          )}

          {formData.HeroImg && formData.HeroImg instanceof File && (
            <img
              src={URL.createObjectURL(formData.HeroImg)}
              alt={formData.HeroAltText || "Hero Preview"}
              className="w-56 h-56 mt-4 object-cover rounded-lg border shadow"
            />
          )}

          <label className="block mb-1 font-medium text-gray-800 mt-2">
            Hero Image Alt Text <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="HeroAltText"
            name="HeroAltText"
            placeholder="Describe the hero image for accessibility"
            value={formData.HeroAltText || ""}
            onChange={(e) => handleInputChange(e, null, null)}
            className={`border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
              errors.HeroAltText ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.HeroAltText && <p className="text-red-600 mt-1">{errors.HeroAltText}</p>}
        </div>

        {/* Category */}
        <div className="mb-6">
          <label className="block mb-2 text-lg font-semibold text-gray-700">Category</label>
          <input
            type="text"
            name="Category"
            value={formData.Category}
            onChange={(e) => handleInputChange(e, null, null)}
            placeholder="Enter blog category"
            className={`border rounded-lg p-3 w-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
              errors.Category ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.Category && <p className="text-red-600 mt-1">{errors.Category}</p>}
        </div>

        {/* SubCategory, Title, Subtitle, Slug */}
        {[
          { label: "Sub Category", name: "SubCategory", placeholder: "Enter sub category"},
          { label: "Tags", name: "Tags", placeholder: "Enter Tag"},
          { label: "Title", name: "Title" },
          { label: "Subtitle", name: "Subtitle" },
          { label: "Slug", name: "Slug", placeholder: "Enter URL-friendly slug" },
        ].map((field, i) => (
          <div key={i} className="mb-6">
            <label className="block mb-2 text-lg font-semibold text-gray-700">{field.label}</label>
            <input
              name={field.name}
              value={formData[field.name]}
              onChange={(e) => handleInputChange(e, null, null)}
              placeholder={field.placeholder || ""}
              className={`border rounded-lg p-3 w-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                errors[field.name] ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors[field.name] && <p className="text-red-600 mt-1">{errors[field.name]}</p>}
          </div>
        ))}

        {/* SEO Meta */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">SEO Meta Information</h2>

          <div className="mb-4">
            <label className="block mb-2 text-lg font-semibold text-gray-700">Meta Title</label>
            <input
              name="MetaTitle"
              value={formData.MetaTitle}
              onChange={(e) => handleInputChange(e, null, null)}
              placeholder="Enter SEO meta title"
              className={`border rounded-lg p-3 w-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                errors.MetaTitle ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.MetaTitle && <p className="text-red-600 mt-1">{errors.MetaTitle}</p>}
          </div>

          <div className="mb-4">
            <label className="block mb-2 text-lg font-semibold text-gray-700">Meta Description</label>
            <textarea
              name="MetaDescription"
              value={formData.MetaDescription}
              onChange={(e) => handleInputChange(e, null, null)}
              className={`border rounded-lg p-3 w-full text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                errors.MetaDescription ? "border-red-500" : "border-gray-300"
              }`}
              rows={3}
              placeholder="Enter SEO meta description"
            />
            {errors.MetaDescription && <p className="text-red-600 mt-1">{errors.MetaDescription}</p>}
          </div>

          <div className="mb-4">
            <label className="block mb-2 text-lg font-semibold text-gray-700">
              Meta Keywords <span className="text-sm text-gray-500">(Comma separated)</span>
            </label>
            <input
              name="MetaKeywords"
              value={formData.MetaKeywords}
              onChange={(e) => handleInputChange(e, null, null)}
              placeholder="Example: marketing, SEO, blog"
              className={`border rounded-lg p-3 w-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                errors.MetaKeywords ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.MetaKeywords && <p className="text-red-600 mt-1">{errors.MetaKeywords}</p>}
          </div>
        </div>

        {/* Content Sections */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Blog Content</h2>

          {formData.Content.map((content, index) => (
            <ContentSection
              key={index}
              index={index}
              content={content}
              onEditorChange={handleEditorChange}
              onImageChange={handleImageChange}
              onAltTextChange={handleAltTextChange}
              onDelete={handleDeleteContent}
              showDelete={formData.Content.length > 1}
              error={errors[`ContentAltText_${index}`]}
            />
          ))}

          <button
            type="button"
            onClick={handleAddContent}
            className="mt-4 bg-indigo-600 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-indigo-700 transition"
          >
            Add More Content
          </button>
        </div>

        {/* FAQs */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Frequently Asked Questions</h2>

          {formData.FAQs.map((faq, index) => (
            <FAQSection
              key={index}
              index={index}
              faq={faq}
              onInputChange={handleInputChange}
              onDelete={handleDeleteFAQ}
              showDelete={formData.FAQs.length > 1}
            />
          ))}

          <button
            type="button"
            onClick={handleAddFAQ}
            className="mt-4 bg-indigo-600 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-green-700 transition"
          >
            Add More FAQs
          </button>
        </div>

        {/* Publishing Options (status + scheduled date) */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Publishing Options</h2>

          <div className="mb-6">
            <label className="block mb-2 text-lg font-semibold text-gray-700">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={(e) => handleInputChange(e, null, null)}
              className={`border rounded-lg p-3 w-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                errors.status ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="draft">Save as Draft</option>
              <option value="published">Publish Now</option>
              <option value="scheduled">Schedule</option>
            </select>
            {errors.status && <p className="text-red-600 mt-1">{errors.status}</p>}
          </div>

          {formData.status === "scheduled" && (
            <div className="mb-6">
    <label className="block mb-2 text-lg font-semibold text-gray-700">
      Scheduled Date
    </label>
    <input
      type="datetime-local"
      name="Date"
      value={formData.Date}
      onChange={(e) => handleInputChange(e, null, null)}
      className={`border rounded-lg p-3 w-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
        errors.Date ? "border-red-500" : "border-gray-300"
      }`}
    />
    {errors.Date && <p className="text-red-600 mt-1">{errors.Date}</p>}
  </div>
          )}
        </div>

        {/* Submit / Update Button */}
        <button
          type="button"
          className={`flex items-center justify-center gap-2 text-xl font-semibold px-8 py-4 rounded-xl shadow-xl transition ${
            loading ? "bg-gray-500 cursor-not-allowed" : "bg-indigo-900 hover:bg-indigo-950 text-white"
          }`}
          onClick={onSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Updating...
            </>
          ) : (
            formData.status === "draft" ? "Save as Draft" :
            formData.status === "published" ? "Publish Blog" : "Schedule Blog"
          )}
        </button>
      </div>

      {/* Popup */}
      <Popup
        open={popup.open}
        type={popup.type}
        message={popup.message}
        onClose={() => setPopup((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
}

// ContentSection component (reusable)
function ContentSection({ index, content, onEditorChange, onImageChange, onAltTextChange, onDelete, showDelete, error }) {
  return (
    <div className="mb-8 border rounded-lg p-6 shadow-sm bg-gray-50 relative">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-xl border-b pb-2">Content {index + 1}</h3>
        {showDelete && (
          <button type="button" onClick={() => onDelete(index)} className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition" aria-label="Delete content section">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <ReactQuill
        theme="snow"
        value={content.html}
        onChange={(value) => onEditorChange(value, index)}
        className="mb-3 bg-white p-2 rounded"
        modules={QUILL_MODULES}
        formats={QUILL_FORMATS}
      />

      <label className="block mb-1 text-gray-700 font-medium">Add Image (optional)</label>
      <input type="file" accept="image/*" onChange={(e) => onImageChange(e, index, "Content")} className="mb-3" />

      {content.img && (
        <>
          {/* if existing remote url (string), show that; if File show preview */}
          {typeof content.img === "string" && (
            <img src={content.img} alt={content.altText || `Content Image ${index + 1}`} className="w-48 h-48 mb-3 object-cover rounded-lg border shadow" />
          )}
          {content.img instanceof File && (
            <img src={URL.createObjectURL(content.img)} alt={content.altText || `Content Preview ${index + 1}`} className="w-48 h-48 mb-3 object-cover rounded-lg border shadow" />
          )}

          <label className="block mb-1 font-medium text-gray-800">Image Alt Text <span className="text-red-500">*</span></label>
          <input
            type="text"
            name="altText"
            placeholder="Describe the image for accessibility"
            value={content.altText}
            onChange={(e) => onAltTextChange(e, index)}
            className={`border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 ${error ? "border-red-500" : "border-gray-300"}`}
          />
          {error && <p className="text-red-600 mt-1">{error}</p>}
        </>
      )}
    </div>
  );
}

// FAQSection component (reusable)
function FAQSection({ index, faq, onInputChange, onDelete, showDelete }) {
  return (
    <div className="mb-6 border rounded-lg p-4 shadow-sm bg-gray-50 relative">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold border-b pb-1">FAQ {index + 1}</h3>
        {showDelete && (
          <button type="button" onClick={() => onDelete(index)} className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition" aria-label="Delete FAQ section">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <input
        name="Q"
        placeholder="Question"
        value={faq.Q}
        onChange={(e) => onInputChange(e, index, "FAQs")}
        className="border border-gray-300 rounded-lg p-3 w-full mb-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />

      <input
        name="A"
        placeholder="Answer"
        value={faq.A}
        onChange={(e) => onInputChange(e, index, "FAQs")}
        className="border border-gray-300 rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    </div>
  );
}
